import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";

import axios from "../utils/axios";
import { showErrorSnackbar } from "../redux/slices/snackbar";
import { saveFile } from "../utils/common";

export const formatDate = (value) => (value ? new Date(value).toLocaleString("en-IN") : "");

export const parseJson = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (e) {
    return fallback;
  }
};

/** Backed-up rows of one table, loaded part by part. */
function TableBackup({ historyUrl, table }) {
  const dispatch = useDispatch();
  const [chunk, setChunk] = useState(0);
  const [page, setPage] = useState(null);

  useEffect(() => {
    setPage(null);
    axios
      .get(historyUrl + "/data", { params: { table, chunk } })
      .then((response) => setPage(response.data))
      .catch((err) => dispatch(showErrorSnackbar(err.message || "Failed to load backup")));
  }, [historyUrl, table, chunk]);

  if (!page) {
    return <CircularProgress size={24} />;
  }

  return (
    <React.Fragment>
      <Box
        component="pre"
        sx={{
          m: 0,
          maxHeight: 300,
          overflow: "auto",
          fontSize: 12,
          backgroundColor: "#f5f5f5",
          p: 2,
        }}
      >
        {JSON.stringify(parseJson(page.data, []), null, 2)}
      </Box>
      {page.chunks > 1 && (
        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} mt={1}>
          <Button size="small" disabled={chunk === 0} onClick={() => setChunk(chunk - 1)}>
            Previous
          </Button>
          <Typography variant="body2">
            Part {chunk + 1} of {page.chunks}
          </Typography>
          <Button
            size="small"
            disabled={chunk + 1 >= page.chunks}
            onClick={() => setChunk(chunk + 1)}
          >
            Next
          </Button>
        </Box>
      )}
    </React.Fragment>
  );
}

/**
 * Details of one delete: info rows, the deleted data per table and a backup download.
 *
 * @param historyUrl API url of the history entry, e.g. "/cpanel/tenant/delete-history/5"
 * @param info       [[label, value], ...] shown at the top
 */
function DeleteBackupViewer({ historyUrl, info, summary, totalRows, fileName }) {
  const dispatch = useDispatch();
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadBackup = async () => {
    try {
      setIsDownloading(true);
      const response = await axios.get(historyUrl + "/download", {
        responseType: "blob",
        timeout: 10 * 60 * 1000,
      });
      await saveFile(response.data, fileName);
    } catch (err) {
      dispatch(showErrorSnackbar("Failed to download backup"));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <React.Fragment>
      <Table size="small" sx={{ mb: 4 }}>
        <TableBody>
          {info.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell sx={{ width: 160 }}>{label}</TableCell>
              <TableCell sx={{ wordBreak: "break-word" }}>{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="subtitle1">Deleted data ({totalRows} records)</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          endIcon={isDownloading ? <CircularProgress size={16} /> : null}
          disabled={isDownloading}
          onClick={downloadBackup}
        >
          Download backup
        </Button>
      </Box>
      {summary.map((item) => (
        <Accordion key={item.table} disableGutters TransitionProps={{ unmountOnExit: true }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              {item.label} — {item.count}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableBackup historyUrl={historyUrl} table={item.table} />
          </AccordionDetails>
        </Accordion>
      ))}
    </React.Fragment>
  );
}

export default DeleteBackupViewer;
